import { MemberContext } from "../contexts/memberContext";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import type { Member } from "../types/Member";
import { MemberService } from "@/services/MemberService";
import { tprStorage } from "@/core/storage";
import { warn } from "@/core/logger";

interface MemberProviderProps {
  children: ReactNode;
  initialMember?: Member | null;
  initialBookings?: number | 0;
  onRefresh?: () => Promise<{ member: Member; bookings: number }>;
}

export const MemberProvider = ({
  children,
  initialMember = null,
  initialBookings = 0,
  onRefresh,
}: MemberProviderProps) => {
  const [member, setMember] = useState<Member | null>(initialMember);
  const [todaysBookings, setTodaysBookings] = useState(initialBookings);

  // Track the latest member so syncMemberFromServer can compare against it without being
  // re-created on every member change (keeps the callback stable for effect deps).
  const memberRef = useRef<Member | null>(member);
  useEffect(() => {
    memberRef.current = member;
  }, [member]);

  const refreshMember = async () => {
    if (onRefresh) {
      const { member: refreshedMember, bookings } = await onRefresh();
      setMember(refreshedMember);
      setTodaysBookings(bookings);
    }
  };

  const syncMemberFromServer = useCallback(async () => {
    try {
      const fresh = await new MemberService().getMember();
      if (!fresh) return; // 404 / no member — keep what we have

      // Merge over the cached tpruser so login-only gate fields (termsAccepted,
      // annualDeclarationAccepted, currentTermsVersion) survive — /api/me returns only the
      // member document.
      const stored = (tprStorage.get("tpruser") as Record<string, unknown> | undefined) || {};
      const merged = { ...stored, ...fresh } as Member;

      // Skip the write + re-render when nothing actually changed, to avoid churn on every
      // tab focus.
      if (memberRef.current && JSON.stringify(memberRef.current) === JSON.stringify(merged)) {
        return;
      }

      tprStorage.set("tpruser", merged);
      memberRef.current = merged;
      setMember(merged);
    } catch (error) {
      // Never blank the member or force a logout on a transient fetch failure — the next
      // focus / socket event will try again.
      warn("MemberProvider", "syncMemberFromServer failed; keeping cached member", error);
    }
  }, []);

  return (
    <MemberContext.Provider value={{ member, todaysBookings, refreshMember, syncMemberFromServer }}>
      {children}
    </MemberContext.Provider>
  );
};
